"use client";

import { CheckCircle2, History, MinusCircle, PlusCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import type {
  RunCheckRecord,
  RunDiff,
  RunFindingRecord,
  RunRecord,
} from "@/lib/aicheck/history/types";
import { authService } from "@/services/auth";
import { RunDiffNarrative } from "./RunDiffNarrative";

const SEVERITY_CHIP_STYLES: Record<string, string> = {
  critical: "bg-red-100 text-red-700",
  error: "bg-red-100 text-red-700",
  warning: "bg-amber-100 text-amber-700",
  info: "bg-sky-100 text-sky-700",
};

const SEVERITY_SORT: Record<string, number> = {
  critical: 0,
  error: 1,
  warning: 2,
  info: 3,
};

const DIFF_PREVIEW_COUNT = 5;

async function authedFetch(url: string, init?: RequestInit) {
  const token = await authService.getIdToken();
  if (!token) throw new Error("未認証です。再度サインインしてください。");
  return fetch(apiUrl(url), {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${SEVERITY_CHIP_STYLES[severity] ?? "bg-zinc-100 text-zinc-600"}`}
    >
      {severity}
    </span>
  );
}

function FindingCard({ finding }: { finding: RunFindingRecord }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3">
      <div className="flex items-center gap-2">
        <SeverityBadge severity={finding.severity} />
        <span className="text-xs font-medium text-zinc-500">
          {finding.checkPoint || finding.checkId}
        </span>
        {finding.dayNumber != null && (
          <span className="text-xs text-zinc-400">Day {finding.dayNumber}</span>
        )}
        {finding.source === "consistency" && (
          <span className="text-xs text-zinc-400">整合性ルール</span>
        )}
      </div>
      <p className="mt-1.5 whitespace-pre-wrap text-sm text-zinc-800">
        {finding.finding}
      </p>
      {finding.recommendation && (
        <p className="mt-1 whitespace-pre-wrap text-xs text-zinc-500">
          推奨: {finding.recommendation}
        </p>
      )}
    </div>
  );
}

function DiffSection({
  title,
  icon,
  findings,
  emptyText,
  tone,
}: {
  title: string;
  icon: React.ReactNode;
  findings: RunFindingRecord[];
  emptyText: string;
  tone: string;
}) {
  const [expanded, setExpanded] = useState(false);
  // 重症度の高い順に並べ、既定では上位のみ表示（大量指摘の洪水対策）
  const sorted = [...findings].sort(
    (a, b) =>
      (SEVERITY_SORT[a.severity] ?? 9) - (SEVERITY_SORT[b.severity] ?? 9),
  );
  const visible = expanded ? sorted : sorted.slice(0, DIFF_PREVIEW_COUNT);
  const hiddenCount = sorted.length - visible.length;
  const severitySummary = ["critical", "error", "warning", "info"]
    .map((severity) => ({
      severity,
      count: sorted.filter((f) => f.severity === severity).length,
    }))
    .filter(({ count }) => count > 0);

  return (
    <section>
      <h3
        className={`mb-2 flex flex-wrap items-center gap-1.5 text-sm font-semibold ${tone}`}
      >
        {icon}
        {title}
        <span className="text-xs font-normal text-zinc-400">
          {findings.length}件
        </span>
        {findings.length > DIFF_PREVIEW_COUNT && (
          <span className="flex items-center gap-1 text-[11px] font-normal">
            {severitySummary.map(({ severity, count }) => (
              <span
                key={severity}
                className={`rounded px-1.5 py-0.5 ${
                  SEVERITY_CHIP_STYLES[severity] ?? "bg-zinc-100 text-zinc-600"
                }`}
              >
                {severity} {count}
              </span>
            ))}
          </span>
        )}
      </h3>
      {findings.length === 0 ? (
        <p className="text-xs text-zinc-400">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {visible.map((finding) => (
            <FindingCard key={finding.findingId} finding={finding} />
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="w-full rounded-lg border border-dashed border-zinc-300 py-2 text-xs text-zinc-500 hover:bg-zinc-50"
            >
              残り {hiddenCount} 件を表示（重要度の高い順に表示しています）
            </button>
          )}
          {expanded && sorted.length > DIFF_PREVIEW_COUNT && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="w-full rounded-lg border border-dashed border-zinc-200 py-1.5 text-[11px] text-zinc-400 hover:bg-zinc-50"
            >
              折りたたむ
            </button>
          )}
        </div>
      )}
    </section>
  );
}

interface RunDetail {
  run: RunRecord;
  checks: RunCheckRecord[];
  findings: RunFindingRecord[];
  diff: RunDiff;
}

/**
 * 実行履歴タイムライン + 選択 run の新規/継続/解消 diff。
 * 案件ワークスペースとメイン画面の「差分・履歴」タブで共用（ia-ui Step1）。
 */
export function RunHistoryPanel({
  projectParam,
  refreshKey,
}: {
  /** project key または表示 ID（API 側で解決） */
  projectParam: string;
  /** 変更で再フェッチ（チェック完了時など） */
  refreshKey?: string | number;
}) {
  const [runs, setRuns] = useState<RunRecord[] | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [runDetail, setRunDetail] = useState<RunDetail | null>(null);
  const [diffSeverity, setDiffSeverity] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectParam) return;
    try {
      const res = await authedFetch(
        `/api/aicheck/projects/${encodeURIComponent(projectParam)}`,
      );
      if (!res.ok) {
        setRuns([]);
        return;
      }
      const data = (await res.json()) as { runs?: RunRecord[] };
      setRuns(data.runs ?? []);
      setSelectedRunId((current) => current ?? data.runs?.[0]?.runId ?? null);
    } catch {
      setRuns([]);
    }
  }, [projectParam]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey intentionally triggers refetch
  useEffect(() => {
    setSelectedRunId(null);
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!selectedRunId) {
      setRunDetail(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await authedFetch(
          `/api/aicheck/projects/${encodeURIComponent(projectParam)}/runs/${encodeURIComponent(selectedRunId)}`,
        );
        if (!res.ok) return;
        const data = (await res.json()) as RunDetail;
        if (!cancelled) {
          setRunDetail(data);
          setDiffSeverity(null);
        }
      } catch {
        // 詳細の取得失敗は一覧表示を妨げない
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectParam, selectedRunId]);

  if (runs === null) {
    return (
      <p className="py-8 text-center text-sm text-zinc-400">読み込み中…</p>
    );
  }

  const diffSeverities = runDetail
    ? [
        ...new Set(
          [
            ...runDetail.diff.newFindings,
            ...runDetail.diff.continuedFindings,
            ...runDetail.diff.resolvedFindings,
          ].map((f) => f.severity),
        ),
      ]
        .sort((a, b) => (SEVERITY_SORT[a] ?? 9) - (SEVERITY_SORT[b] ?? 9))
        .map((severity) => ({
          severity,
          count: [
            ...runDetail.diff.newFindings,
            ...runDetail.diff.continuedFindings,
            ...runDetail.diff.resolvedFindings,
          ].filter((f) => f.severity === severity).length,
        }))
    : [];
  const filterBySeverity = (findings: RunFindingRecord[]) =>
    diffSeverity
      ? findings.filter((f) => f.severity === diffSeverity)
      : findings;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
      {/* Run timeline */}
      <div>
        <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-zinc-700">
          <History size={15} />
          実行履歴
        </h2>
        {runs.length === 0 ? (
          <p className="text-xs text-zinc-400">まだ実行がありません。</p>
        ) : (
          <div className="space-y-1.5">
            {runs.map((run) => (
              <button
                key={run.runId}
                type="button"
                onClick={() => setSelectedRunId(run.runId)}
                className={`w-full rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                  run.runId === selectedRunId
                    ? "border-indigo-400 bg-indigo-50"
                    : "border-zinc-200 bg-white hover:bg-zinc-50"
                }`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="font-medium text-zinc-800">
                    {formatDateTime(run.startedAt)}
                  </span>
                  <span className="flex items-center gap-1">
                    {run.trigger === "freeform" && (
                      <span
                        className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
                        title="フリーフォームレビューの記録（差分計算・通知の対象外）"
                      >
                        フリーフォーム
                      </span>
                    )}
                    {run.trigger === "date" && (
                      <span
                        className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
                        title="出発日接近の自動チェック"
                      >
                        自動
                      </span>
                    )}
                    <span
                      className={
                        run.status === "completed"
                          ? "text-emerald-600"
                          : run.status === "running" ||
                              run.status === "interrupted"
                            ? "text-amber-600"
                            : "text-red-600"
                      }
                      title={
                        run.status === "interrupted"
                          ? "サーバー再起動（再デプロイ等）により中断されました。再実行してください"
                          : undefined
                      }
                    >
                      {run.status === "running"
                        ? "実行中"
                        : run.status === "interrupted"
                          ? "再起動により中断"
                          : run.status}
                    </span>
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-zinc-500">
                  {run.overallScore != null && (
                    <span title="AIチェックの総合評価点（100点満点。高いほど指摘が少ない/軽い）">
                      総合 {run.overallScore}点
                    </span>
                  )}
                  <span
                    className="text-emerald-600"
                    title={`新規の指摘 ${run.newCount} 件`}
                  >
                    新規{run.newCount}
                  </span>
                  <span
                    className="text-zinc-500"
                    title={`前回から継続中の指摘 ${run.continuedCount} 件`}
                  >
                    継続{run.continuedCount}
                  </span>
                  <span
                    className="text-sky-600"
                    title={`解消された指摘 ${run.resolvedCount} 件`}
                  >
                    解消{run.resolvedCount}
                  </span>
                  {run.executedBy && (
                    <span className="truncate">{run.executedBy}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Run detail: diff */}
      <div>
        {runDetail ? (
          <div className="space-y-6">
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-semibold text-zinc-800">
                  {formatDateTime(runDetail.run.startedAt)} の実行
                </span>
                {runDetail.run.overallScore != null && (
                  <span
                    className="text-zinc-600"
                    title="AIチェックの総合評価点（100点満点。高いほど指摘が少ない/軽い）"
                  >
                    総合 {runDetail.run.overallScore}点
                  </span>
                )}
                <span className="text-xs text-zinc-400">
                  チェック {runDetail.checks.length} 件 / 指摘{" "}
                  {runDetail.findings.length} 件
                </span>
                {runDetail.diff.previousRunId == null && (
                  <span className="text-xs text-zinc-400">
                    （初回実行のため全件を新規として表示）
                  </span>
                )}
                {runDetail.run.configDigest && (
                  <span
                    className="font-mono text-[10px] text-zinc-300"
                    title="実行時の設定 / 知識状態の識別子。変更の前後で結果を比較するときの目印"
                  >
                    {runDetail.run.configDigest.slice(0, 6)}/
                    {runDetail.run.knowledgeDigest?.slice(0, 6) ?? "—"}
                  </span>
                )}
              </div>
              {diffSeverities.length > 1 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-zinc-400">絞り込み:</span>
                  {diffSeverities.map(({ severity, count }) => (
                    <button
                      key={severity}
                      type="button"
                      onClick={() =>
                        setDiffSeverity((current) =>
                          current === severity ? null : severity,
                        )
                      }
                      className={`rounded px-2 py-0.5 text-[11px] transition-opacity ${
                        SEVERITY_CHIP_STYLES[severity] ??
                        "bg-zinc-100 text-zinc-600"
                      } ${
                        diffSeverity && diffSeverity !== severity
                          ? "opacity-30"
                          : ""
                      }`}
                      title={`${severity} のみ表示（再クリックで解除）`}
                    >
                      {severity} {count}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <RunDiffNarrative
              runId={selectedRunId}
              previousRunId={runDetail.diff.previousRunId}
            />

            <DiffSection
              title={
                runDetail.diff.previousRunId == null
                  ? "指摘一覧（初回実行）"
                  : "新規の指摘"
              }
              icon={<PlusCircle size={15} />}
              tone="text-emerald-700"
              findings={filterBySeverity(runDetail.diff.newFindings)}
              emptyText="新規の指摘はありません。"
            />
            {runDetail.diff.previousRunId != null && (
              <>
                <DiffSection
                  title="継続中の指摘"
                  icon={<MinusCircle size={15} />}
                  tone="text-zinc-600"
                  findings={filterBySeverity(runDetail.diff.continuedFindings)}
                  emptyText="継続中の指摘はありません。"
                />
                <DiffSection
                  title="解消された指摘"
                  icon={<CheckCircle2 size={15} />}
                  tone="text-sky-700"
                  findings={filterBySeverity(runDetail.diff.resolvedFindings)}
                  emptyText="解消された指摘はありません。"
                />
              </>
            )}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-zinc-400">
            左の実行履歴から選択してください。
          </p>
        )}
      </div>
    </div>
  );
}
